import type { Circuit, CircuitRoadPiece, TrackCell, ValidationResult } from '../types/track.js';
import { Direction } from '../types/track.js';
import { getCorePath } from './circuitEdit.js';

function newRoadPieceId(): string {
  return `rp-${Math.random().toString(36).slice(2, 10)}`;
}

function pieceShape(piece: CircuitRoadPiece): 'straight' | 'corner' {
  return piece.shape ?? 'straight';
}

function getRoadPieceSourceCells(
  circuit: Circuit,
  source: CircuitRoadPiece['source']
): TrackCell[] {
  if (source === 'path') return getCorePath(circuit.path);
  if (source === 'jokerLap') return circuit.jokerLap?.cells ?? [];
  return circuit.startLane?.cells ?? [];
}

/**
 * Décalages relatifs à `startIndex` couverts par la pièce, dans l'ordre (voir
 * `CircuitRoadPiece.shape`) — décalage 0 toujours présent et correspondant à
 * `startIndex` lui-même (la case "ancre" : premier bras d'une ligne droite,
 * case de virage elle-même pour un virage). Exporté pour le rendu (voir
 * `track3dLayout.ts`), qui n'affiche qu'un seul modèle sur cette case ancre
 * plutôt qu'une copie par case couverte.
 */
export function coveredOffsets(piece: CircuitRoadPiece): number[] {
  if (pieceShape(piece) === 'corner') {
    const arm = (piece.cellCost - 1) / 2;
    if (!Number.isInteger(arm) || arm < 0) return [];
    const offsets: number[] = [];
    for (let i = -arm; i <= arm; i++) offsets.push(i);
    return offsets;
  }
  const offsets: number[] = [];
  for (let i = 0; i < piece.cellCost; i++) offsets.push(i);
  return offsets;
}

/**
 * Résout les cases réellement couvertes par une pièce groupée. Pour `source:
 * 'path'` sur un tracé fermé, l'index peut franchir la couture (dernier index
 * → 0) : on boucle modulo la longueur, comme `normalizeMainPathIndex`. Les
 * branches `jokerLap`/`startLane` ne bouclent jamais (ce ne sont pas des boucles).
 * Une pièce "corner" est centrée sur `startIndex` (la case de virage elle-même) :
 * ses décalages s'étendent symétriquement de part et d'autre (voir `coveredOffsets`).
 */
export function resolveRoadPieceCells(
  circuit: Circuit,
  piece: CircuitRoadPiece
): TrackCell[] | null {
  const source = getRoadPieceSourceCells(circuit, piece.source);
  const len = source.length;
  if (len === 0 || piece.startIndex < 0 || piece.cellCost < 1) return null;
  const offsets = coveredOffsets(piece);
  if (offsets.length !== piece.cellCost) return null;

  if (piece.source === 'path') {
    if (piece.cellCost > len) return null;
    return offsets.map((o) => source[(((piece.startIndex + o) % len) + len) % len]);
  }

  const from = piece.startIndex + offsets[0];
  const to = piece.startIndex + offsets[offsets.length - 1];
  if (from < 0 || to >= len) return null;
  return offsets.map((o) => source[piece.startIndex + o]);
}

function directionFromDelta(dx: number, dy: number): Direction | null {
  if (dx === 1 && dy === 0) return Direction.EAST;
  if (dx === -1 && dy === 0) return Direction.WEST;
  if (dx === 0 && dy === 1) return Direction.SOUTH;
  if (dx === 0 && dy === -1) return Direction.NORTH;
  return null;
}

/** Direction constante d'une run de cases (null si la run n'est pas rectiligne). */
function straightRunDirection(cells: TrackCell[]): Direction | null {
  if (cells.length < 2) return null;
  const dx = cells[1].x - cells[0].x;
  const dy = cells[1].y - cells[0].y;
  const dir = directionFromDelta(dx, dy);
  if (dir === null) return null;
  for (let i = 1; i < cells.length - 1; i++) {
    if (cells[i + 1].x - cells[i].x !== dx || cells[i + 1].y - cells[i].y !== dy) return null;
  }
  return dir;
}

/**
 * Une run "corner" est valide si chaque bras (avant/après la case centrale) est
 * rectiligne et si les deux bras vont bien dans des directions différentes (un
 * vrai virage au centre, pas une ligne droite déguisée). Une pièce à 1 case
 * (pas de bras) est toujours valide — comme aujourd'hui pour les virages simples.
 */
function cornerRunIsStraightOnBothArms(cells: TrackCell[]): boolean {
  if (cells.length === 1) return true;
  const mid = (cells.length - 1) / 2;
  if (!Number.isInteger(mid)) return false;
  const armIn = cells.slice(0, mid + 1);
  const armOut = cells.slice(mid);
  const dirIn = straightRunDirection(armIn);
  const dirOut = straightRunDirection(armOut);
  if (dirIn === null || dirOut === null) return false;
  return dirIn !== dirOut;
}

function roadPieceKey(piece: CircuitRoadPiece): string {
  return `${piece.source}:${piece.startIndex}:${pieceShape(piece)}`;
}

/**
 * Une pièce groupée est valide si ses cases se résolvent et forment une run
 * géométriquement cohérente avec sa forme : rectiligne pour `shape: 'straight'`
 * (avec rotation alignée sur la direction réelle), ou un virage net à un seul
 * changement de direction pour `shape: 'corner'` (la rotation d'un virage est
 * déjà entièrement déterminée par la géométrie du tracé au rendu — voir
 * `track3dLayout.ts#computeTile` — donc non revérifiée ici, comme pour le cas
 * 1-case déjà existant). Ne revalide pas la fermeture/connectivité du circuit
 * lui-même (déjà garanti par `validateManualCircuitEdit` sur les cases
 * sous-jacentes, inchangées).
 */
function isRoadPieceGeometryValid(circuit: Circuit, piece: CircuitRoadPiece): boolean {
  const cells = resolveRoadPieceCells(circuit, piece);
  if (!cells || cells.length !== piece.cellCost) return false;
  if (pieceShape(piece) === 'corner') return cornerRunIsStraightOnBothArms(cells);
  if (cells.length === 1) return true; // une seule case : toujours "droite"
  const dir = straightRunDirection(cells);
  return dir !== null && dir === piece.rotation;
}

export function findRoadPieceAt(circuit: Circuit, x: number, y: number): CircuitRoadPiece | null {
  for (const piece of circuit.roadPieces ?? []) {
    const cells = resolveRoadPieceCells(circuit, piece);
    if (cells?.some((cell) => cell.x === x && cell.y === y)) return piece;
  }
  return null;
}

/** Clés d'occupation (`source:index`) réellement couvertes par une pièce, cases
 * de `source: 'path'` bouclées modulo la longueur de la source (comme `resolveRoadPieceCells`). */
function pieceOccupancyKeys(circuit: Circuit, piece: CircuitRoadPiece): string[] {
  const len = getRoadPieceSourceCells(circuit, piece.source).length;
  const offsets = coveredOffsets(piece);
  return offsets.map((o) => {
    const idx =
      piece.source === 'path' && len > 0
        ? (((piece.startIndex + o) % len) + len) % len
        : piece.startIndex + o;
    return `${piece.source}:${idx}`;
  });
}

/** Pré-vérification avant regroupement (hors chevauchement avec une pièce existante). */
export function canGroupRoadPieceRun(
  circuit: Circuit,
  source: CircuitRoadPiece['source'],
  startIndex: number,
  cellCost: number,
  shape: CircuitRoadPiece['shape'] = 'straight'
): boolean {
  const candidate: CircuitRoadPiece = {
    id: '',
    source,
    startIndex,
    cellCost,
    modelId: '',
    rotation: Direction.NORTH,
    shape,
  };
  const cells = resolveRoadPieceCells(circuit, candidate);
  if (!cells || cells.length !== cellCost) return false;
  if (shape === 'corner') {
    if (!cornerRunIsStraightOnBothArms(cells)) return false;
  } else if (cells.length > 1 && straightRunDirection(cells) === null) {
    return false;
  }

  const usedKeys = new Set<string>();
  for (const piece of circuit.roadPieces ?? []) {
    for (const key of pieceOccupancyKeys(circuit, piece)) usedKeys.add(key);
  }
  for (const key of pieceOccupancyKeys(circuit, candidate)) {
    if (usedKeys.has(key)) return false;
  }
  return roadPieceKey(candidate).length > 0;
}

export function addRoadPiece(
  circuit: Circuit,
  source: CircuitRoadPiece['source'],
  startIndex: number,
  cellCost: number,
  modelId: string,
  rotation: Direction,
  shape: CircuitRoadPiece['shape'] = 'straight'
): { circuit: Circuit } | { error: string } {
  if (!canGroupRoadPieceRun(circuit, source, startIndex, cellCost, shape)) {
    return { error: 'Regroupement de pièce route invalide' };
  }
  const piece: CircuitRoadPiece = {
    id: newRoadPieceId(),
    source,
    startIndex,
    cellCost,
    modelId,
    rotation,
    shape,
  };
  if (!isRoadPieceGeometryValid(circuit, piece)) {
    return { error: 'Orientation incohérente avec la direction réelle du tracé' };
  }
  return {
    circuit: {
      ...circuit,
      roadPieces: [...(circuit.roadPieces ?? []), piece],
    },
  };
}

export function removeRoadPiece(circuit: Circuit, pieceId: string): Circuit {
  const roadPieces = (circuit.roadPieces ?? []).filter((piece) => piece.id !== pieceId);
  return {
    ...circuit,
    roadPieces: roadPieces.length > 0 ? roadPieces : undefined,
  };
}

/** Supprime les pièces dont la géométrie sous-jacente a changé (édition ultérieure du tracé). */
export function pruneRoadPieces(circuit: Circuit): Circuit {
  const seen = new Set<string>();
  const roadPieces = (circuit.roadPieces ?? []).filter((piece) => {
    if (!isRoadPieceGeometryValid(circuit, piece)) return false;
    let overlaps = false;
    for (const key of pieceOccupancyKeys(circuit, piece)) {
      if (seen.has(key)) overlaps = true;
      seen.add(key);
    }
    return !overlaps;
  });
  return {
    ...circuit,
    roadPieces: roadPieces.length > 0 ? roadPieces : undefined,
  };
}

/**
 * Validation des regroupements de pièces route. La connectivité / boucle /
 * ligne de départ sont déjà garanties par `validateManualCircuitEdit` sur les
 * cases sous-jacentes (une pièce n'est qu'une étiquette cosmétique dessus) :
 * seule la cohérence interne du regroupement est vérifiée ici.
 */
export function validateRoadPieces(circuit: Circuit): ValidationResult {
  const errors: string[] = [];
  const seen = new Set<string>();

  for (const piece of circuit.roadPieces ?? []) {
    const cells = resolveRoadPieceCells(circuit, piece);
    if (!cells) {
      errors.push(`Pièce route invalide : plage de cases hors limites (${piece.id})`);
      continue;
    }
    if (cells.length !== piece.cellCost) {
      errors.push(`Pièce route invalide : coût en cases incohérent (${piece.id})`);
    }
    if (pieceShape(piece) === 'corner') {
      if (!cornerRunIsStraightOnBothArms(cells)) {
        errors.push(`Pièce route invalide : virage incohérent avec le tracé (${piece.id})`);
      }
    } else if (cells.length > 1 && straightRunDirection(cells) === null) {
      errors.push(
        `Pièce route invalide : les cases regroupées ne sont pas rectilignes (${piece.id})`
      );
    } else if (cells.length > 1 && straightRunDirection(cells) !== piece.rotation) {
      errors.push(`Pièce route invalide : rotation incohérente avec le tracé (${piece.id})`);
    }
    for (const key of pieceOccupancyKeys(circuit, piece)) {
      if (seen.has(key)) {
        errors.push(`Pièce route invalide : chevauchement avec une autre pièce (${piece.id})`);
      }
      seen.add(key);
    }
  }

  return { isValid: errors.length === 0, errors };
}
